import { GET_WISHLIST_ITEMS } from '@/common/hooks/useCustomerWishlistSkus/customerWishlist.gql.ce';
import { useUserContext } from '@/context/user';
import mergeOperations from '@/util/shallowMerge';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import defaultOperations from '../addToListButton.gql';

export const useSingleWishlist = props => {
  const { afterAdd, beforeAdd, item, afterLoginInCartPage } = props;

  const operations = mergeOperations(defaultOperations, props.operations);
  const router = useRouter();
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [loginConfirmLoading, setLoginConfirmLoading] = useState(false);
  const [addProductToWishlist, { data: addProductData, error: errorAddingProduct, loading: isAddingToWishlist }] =
    useMutation(operations.addProductToWishlistMutation);

  const { client, data, error } = useQuery(operations.getProductsInWishlistsQuery);

  const [reqCustomerWishlistItems] = useLazyQuery(GET_WISHLIST_ITEMS, {
    fetchPolicy: 'cache-and-network',
  });

  const { customerWishlistProducts } = data || {};

  const isSelected = useMemo(() => {
    return customerWishlistProducts?.includes(item.sku) || isAddingToWishlist;
  }, [customerWishlistProducts, isAddingToWishlist, item.sku]);

  const [{ isSignedIn }] = useUserContext();

  const handleClick = async () => {
    if (!isSignedIn) {
      setOpenLoginDialog(true);
      return;
    }

    if (isSelected) {
      router.push('/my-showroom');
      return;
    }

    try {
      if (beforeAdd) {
        await beforeAdd();
      }

      await addProductToWishlist({
        variables: {
          wishlistId: '0',
          itemOptions: item,
        },
      });

      client.writeQuery({
        query: operations.getProductsInWishlistsQuery,
        data: {
          customerWishlistProducts: [...customerWishlistProducts, item.sku],
        },
      });

      if (afterAdd) {
        afterAdd();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const successToastProps = useMemo(() => {
    if (addProductData) {
      return {
        type: 'success',
        message: 'Item successfully added to your favorites list.',
        timeout: 5000,
      };
    }

    return null;
  }, [addProductData]);

  const errorToastProps = useMemo(() => {
    if (errorAddingProduct) {
      return {
        type: 'error',
        message: 'Something went wrong adding the product to your wishlist.',
        timeout: 5000,
      };
    }

    return null;
  }, [errorAddingProduct]);

  const afterLogin = async loginOk => {
    setLoginConfirmLoading(true);

    if (!loginOk) {
      setLoginConfirmLoading(false);
      return;
    }

    let currentPage = 1;
    let shouldFetchMore = true;

    while (shouldFetchMore) {
      shouldFetchMore = false;

      try {
        const customerWishlistRes = await reqCustomerWishlistItems({ variables: { currentPage } });
        const wishlists = customerWishlistRes?.data?.customer?.wishlists;

        if (!Array.isArray(wishlists)) return;

        for (const wishlist of wishlists) {
          const items = wishlist?.items_v2?.items;
          const pageInfo = wishlist?.items_v2?.page_info;

          if (!Array.isArray(items)) continue;

          for (const wishlistItem of items) {
            if (wishlistItem?.product?.sku === item.sku) {
              setOpenLoginDialog(false);
              setLoginConfirmLoading(false);
              afterLoginInCartPage?.();
              return;
            }
          }

          if (pageInfo.total_pages > pageInfo.current_page) {
            shouldFetchMore = true;
            currentPage++;
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    addProductToWishlist({
      variables: {
        wishlistId: '0',
        itemOptions: item,
      },
    })
      .then(() => {
        client.writeQuery({
          query: operations.getProductsInWishlistsQuery,
          data: {
            customerWishlistProducts: [...customerWishlistProducts, item.sku],
          },
        });
      })
      .finally(() => {
        setOpenLoginDialog(false);
        setLoginConfirmLoading(false);
        afterLoginInCartPage?.();
      });
  };

  return {
    buttonText: props.buttonText && props.buttonText(isSelected),
    customerWishlistProducts,
    errorToastProps,
    handleClick,
    isSelected,
    successToastProps,
    openLoginDialog,
    setOpenLoginDialog,
    afterLogin,
    loginConfirmLoading,
    setLoginConfirmLoading,
  };
};
